import React, { useCallback, useMemo, useState } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useSpotlight } from '@atlaskit/onboarding';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectPermissions } from '@atlassian/jira-project-permissions-service/src/main.tsx';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { onboarding_issueViewRedaction_RedactionOnboardingSpotlight_issue$key } from '@atlassian/jira-relay/src/__generated__/onboarding_issueViewRedaction_RedactionOnboardingSpotlight_issue.graphql';
import type { onboarding_issueViewRedaction_RedactionOnboardingSpotlight_user$key } from '@atlassian/jira-relay/src/__generated__/onboarding_issueViewRedaction_RedactionOnboardingSpotlight_user.graphql';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	ACTION_MENU_REDACTION,
	REDACTION_MARKER,
	REDACTION_MARKER_VIEWER,
} from '../../common/constants.tsx';
import type { SpotlightType } from '../../common/types.tsx';
import messages from './messages.tsx';
import { getInitialSpotlightType, getTargetField } from './utils.tsx';

type Props = {
	issue: onboarding_issueViewRedaction_RedactionOnboardingSpotlight_issue$key;
	user: onboarding_issueViewRedaction_RedactionOnboardingSpotlight_user$key;
};

export const RedactionOnboardingSpotlight = ({ issue, user }: Props) => {
	const data = useFragment<onboarding_issueViewRedaction_RedactionOnboardingSpotlight_issue$key>(
		graphql`
			fragment onboarding_issueViewRedaction_RedactionOnboardingSpotlight_issue on JiraIssue {
				redactedFields {
					edges {
						node {
							fieldId
							typeKey
						}
					}
				}
			}
		`,
		issue,
	);

	const {
		showRedactionChangeBoardingOnIssueViewAsViewer: showViewerExp,
		showRedactionChangeBoardingOnIssueViewAsEditor: showEditorExp,
	} = useFragment<onboarding_issueViewRedaction_RedactionOnboardingSpotlight_user$key>(
		graphql`
			fragment onboarding_issueViewRedaction_RedactionOnboardingSpotlight_user on JiraUserPreferences {
				showRedactionChangeBoardingOnIssueViewAsViewer
				showRedactionChangeBoardingOnIssueViewAsEditor
			}
		`,
		user,
	);

	const [commitViewerExp] = useMutation(graphql`
		mutation onboarding_setShowRedactionChangeBoardingOnIssueViewAsViewerMutation(
			$cloudId: ID!
			$show: Boolean!
		) {
			jira {
				userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
					setShowRedactionChangeBoardingOnIssueViewAsViewer(show: $show)
				}
			}
		}
	`);

	const [commitEditorExp] = useMutation(graphql`
		mutation onboarding_setShowRedactionChangeBoardingOnIssueViewAsEditorMutation(
			$cloudId: ID!
			$show: Boolean!
		) {
			jira {
				userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
					setShowRedactionChangeBoardingOnIssueViewAsEditor(show: $show)
				}
			}
		}
	`);

	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const [permissions] = useProjectPermissions(projectKey);
	const { isTargetRendered } = useSpotlight();
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const [activeSpotlight, setActiveSpotlight] = useState<SpotlightType>(
		getInitialSpotlightType(permissions.canEditIssues, !!showViewerExp, !!showEditorExp),
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const redactedFields = useMemo(() => {
		if (data.redactedFields?.edges) {
			return data.redactedFields.edges
				.map((edge) => {
					if (edge?.node) {
						const { fieldId, typeKey } = edge.node;
						return { fieldId, typeKey };
					}
					return null;
				})
				.filter(Boolean);
		}
		return [];
	}, [data?.redactedFields?.edges]);

	const target = useMemo(() => getTargetField(redactedFields), [redactedFields]);

	const onNext = useCallback(() => {
		setActiveSpotlight(ACTION_MENU_REDACTION);
	}, [setActiveSpotlight]);

	const onReadMore = useCallback(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window?.open(
			'https://support.atlassian.com/jira-software-cloud/docs/how-redaction-works/',
			'_blank',
		);
	}, []);

	const onFinish = useCallback(() => {
		setActiveSpotlight(null);
		fireTrackAnalytics(createAnalyticsEvent({}), 'redactionChangeBoarding completed');
		if (permissions.canEditIssues) {
			commitEditorExp({
				variables: {
					cloudId,
					show: false,
				},
			});
		}
		commitViewerExp({
			variables: {
				cloudId,
				show: false,
			},
		});
	}, [cloudId, commitEditorExp, commitViewerExp, createAnalyticsEvent, permissions.canEditIssues]);

	const renderSpotlight = useCallback(() => {
		if (activeSpotlight === null || target === null) {
			return null;
		}

		if (
			activeSpotlight === REDACTION_MARKER &&
			isTargetRendered(`${REDACTION_MARKER}-${target}`) &&
			isTargetRendered(ACTION_MENU_REDACTION)
		) {
			return (
				<JiraSpotlight
					actions={[
						{ onClick: onNext, text: formatMessage(messages.next), autoFocus: true },
						{ onClick: onFinish, text: formatMessage(messages.dismiss) },
					]}
					target={`${REDACTION_MARKER}-${target}`}
					actionsBeforeElement={formatMessage(messages.onboardingStep1)}
					messageType="transactional"
					messageId="issue-view-redaction.ui.onboarding.jira-spotlight.spotlight-redaction-message-marker"
				>
					{formatMessage(messages.spotlightRedactionMessage)}
				</JiraSpotlight>
			);
		}

		if (
			activeSpotlight === REDACTION_MARKER_VIEWER &&
			isTargetRendered(`${REDACTION_MARKER}-${target}`)
		) {
			return (
				<JiraSpotlight
					actions={[
						{ onClick: onFinish, text: formatMessage(messages.ok), autoFocus: true },
						{ onClick: onReadMore, text: formatMessage(messages.readMore) },
					]}
					target={`${REDACTION_MARKER}-${target}`}
					messageType="transactional"
					messageId="issue-view-redaction.ui.onboarding.jira-spotlight.spotlight-redaction-message-marker-viewer"
				>
					{formatMessage(messages.spotlightRedactionMessage)}
				</JiraSpotlight>
			);
		}

		if (activeSpotlight === ACTION_MENU_REDACTION && isTargetRendered(ACTION_MENU_REDACTION)) {
			return (
				<JiraSpotlight
					actions={[
						{ onClick: onFinish, text: formatMessage(messages.ok), autoFocus: true },
						{ onClick: onReadMore, text: formatMessage(messages.readMore) },
					]}
					target={ACTION_MENU_REDACTION}
					messageType="transactional"
					messageId="issue-view-redaction.ui.onboarding.jira-spotlight.spotlight-redaction-message-action-menu"
					actionsBeforeElement={formatMessage(messages.onboardingStep2)}
				>
					{formatMessage(
						fg('jira-issue-terminology-update-m4')
							? messages.spotlightActionMenuMessageIssueTerminologyRefreshM4
							: messages.spotlightActionMenuMessage,
					)}
				</JiraSpotlight>
			);
		}

		return null;
	}, [activeSpotlight, isTargetRendered, target, onNext, formatMessage, onReadMore, onFinish]);

	return <>{renderSpotlight()}</>;
};
